import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { AreaIds } from "../config/assetFieldConfig";
import { useDelayAssetsGrouped } from "@/shared/selectors/AssetButtonGroup";
import { toSlug } from "@/shared/utils/assets";

export const useAssetNavigation = (
	areaIds: AreaIds,
	defaultAssetId: number,
) => {
	const { assetName } = useParams<{ assetName?: string }>();
	const navigate = useNavigate();

	const { data: delayAssets } = useDelayAssetsGrouped();

	const selectedAssetId = useMemo(() => {
		if (!assetName || !delayAssets) return null;
		const match = delayAssets.find((a) => toSlug(a.AssetName) === assetName);
		return match?.AssetId ?? null;
	}, [assetName, delayAssets]);

	const assetId = selectedAssetId ?? defaultAssetId;

	// Auto-redirect to first asset (SAW group first, then by name) when no asset selected
	useEffect(() => {
		if (assetName || !delayAssets?.length) return;
		const groupOrder = (parentId: number | null) => {
			if (parentId === areaIds.saws) return 0;
			if (parentId === areaIds.welds) return 1;
			return 2;
		};
		const sorted = [...delayAssets].sort((a, b) => {
			const go = groupOrder(a.ParentAssetId) - groupOrder(b.ParentAssetId);
			if (go !== 0) return go;
			return a.AssetName.localeCompare(b.AssetName);
		});
		navigate(`/work-orders/${toSlug(sorted[0].AssetName)}`, { replace: true });
	}, [assetName, delayAssets, navigate, areaIds]);

	const parentAssetId = useMemo(() => {
		if (!delayAssets) return null;
		const match = delayAssets.find((a) => a.AssetId === assetId);
		return match?.ParentAssetId ?? null;
	}, [assetId, delayAssets]);

	const handleAssetChange = useCallback(
		(slug: string | null) => {
			navigate(slug ? `/work-orders/${slug}` : "/work-orders");
		},
		[navigate],
	);

	return { assetId, assetName, parentAssetId, handleAssetChange, delayAssets };
};
